/**
 * PageHelpProvider — lets deep pages (the creation wizard) publish their current
 * sub-step to the globally-mounted ChatWidget, which lives outside the routed
 * tree. The widget already knows the route via useLocation(); this context adds
 * the editor sub-step so page-aware help can be step-specific.
 *
 * editorStep is an opaque string matching the editor pageId suffixes in
 * client/src/config/pageHelpContext.js: 'details' | 'template' | 'photos' |
 * 'keymoments' | 'keymoments-frames' | 'music' | 'preview', or null when not in
 * the editor. Anything that fails here degrades to null → generic chat.
 */
import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

const PageHelpContext = createContext({
  editorStep: null,
  setEditorStep: () => {},
});

export const PageHelpProvider = ({ children }) => {
  const [editorStep, setEditorStepState] = useState(null);

  // Stable setter that ignores no-op updates (avoids needless re-renders when a
  // step effect re-runs with the same value).
  const setEditorStep = useCallback((next) => {
    setEditorStepState((prev) => (prev === next ? prev : next ?? null));
  }, []);

  const value = useMemo(() => ({ editorStep, setEditorStep }), [editorStep, setEditorStep]);

  return <PageHelpContext.Provider value={value}>{children}</PageHelpContext.Provider>;
};

export const usePageHelp = () => useContext(PageHelpContext);

export default PageHelpContext;
